import React, { useState, useEffect } from 'react';
import { formatAppDate } from '../../services/appConfigRuntime';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Close,
  Share,
  Refresh,
  Architecture,
  Engineering,
  PictureAsPdf,
  Image,
  Comment as CommentIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudUpload,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { filesApi, forgeApi } from '../../services/fileShareApi';
import ForgeViewer from './ForgeViewer';
import { translationStartErrorMessage } from './fileShareTranslationErrors';

const FileViewer = ({ file, onClose, onShare }) => {
  const [viewerUrl, setViewerUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [translationStatus, setTranslationStatus] = useState(file.forgeStatus || file.translationStatus);
  const [uploadProgress, setUploadProgress] = useState(file.uploadProgress || null);
  const [translationProgress, setTranslationProgress] = useState(file.translationProgress || null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(true);
  const [clientApprovedAt, setClientApprovedAt] = useState(file.clientApprovedAt || null);
  const [clientApprovedBy, setClientApprovedBy] = useState(file.clientApprovedBy || null);
  const [uploadedBy, setUploadedBy] = useState(file.uploadedBy || null);
  const [translating, setTranslating] = useState(false);
  const [overageDialog, setOverageDialog] = useState({
    open: false,
    warnings: [],
    confirmationWord: 'APPROVE',
    typedValue: '',
    forceRetranslate: false,
  });
  const [retranslateDialogOpen, setRetranslateDialogOpen] = useState(false);

  const fileType = (file.fileType || file.type || '').toUpperCase();
  const normalizedTranslationStatus = (translationStatus || '').toUpperCase();
  const isCadFile = fileType === 'RVT' || fileType === 'DWG';
  const isTranslationActive =
    normalizedTranslationStatus === 'IN_PROGRESS' ||
    normalizedTranslationStatus === 'TRANSLATING' ||
    normalizedTranslationStatus === 'TRANSLATING...' ||
    normalizedTranslationStatus === 'UPLOADING';
  const isTranslated =
    normalizedTranslationStatus === 'SUCCESS' ||
    normalizedTranslationStatus === 'COMPLETE';
  const shouldShowViewerArea = !isCadFile || isTranslationActive || isTranslated;
  const needsTranslation = isCadFile && !isTranslationActive && !isTranslated;
  const isStorageOnlyFile =
    typeof (file.fileId || file.id) === 'string' &&
    String(file.fileId || file.id).startsWith('storage_');

  useEffect(() => {
    if (file) {
      if (isCadFile && !isTranslationActive && !isTranslated) {
        setViewerUrl(null);
        setError(null);
        setLoading(false);
        return;
      }
      loadViewer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when opening a different file
  }, [file?.fileId || file?.id]);

  useEffect(() => {
    if (!file) return;
    if (isCadFile && isTranslationActive) {
      checkTranslationStatus();
    } else if (isCadFile && isTranslated) {
      setLoading(true);
      loadForgeViewer()
        .catch((err) => setError('Failed to load viewer: ' + (err.message || err)))
        .finally(() => setLoading(false));
    } else if (isCadFile && needsTranslation) {
      setViewerUrl(null);
      setError(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationStatus]);

  useEffect(() => {
    setUploadedBy(file?.uploadedBy || null);
  }, [file?.uploadedBy, file?.fileId, file?.id]);

  useEffect(() => {
    if (file?.forgeStatus || file?.translationStatus) {
      setTranslationStatus(file.forgeStatus || file.translationStatus);
    }
  }, [file?.fileId, file?.id]);

  const fileId = file?.fileId || file?.id;
  useEffect(() => {
    if (!fileId) return;
    const fetchComments = async () => {
      try {
        setCommentsLoading(true);
        const res = await filesApi.getComments(fileId);
        const list = Array.isArray(res.data) ? [...res.data] : [];
        list.sort((a, b) => {
          const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return tb - ta;
        });
        setComments(list);
      } catch (err) {
        console.error('Error fetching comments:', err);
        setComments([]);
      } finally {
        setCommentsLoading(false);
      }
    };
    fetchComments();
    const fetchFileDetails = async () => {
      try {
        const res = await filesApi.getById(fileId);
        if (res.data && res.data.clientApprovedAt != null) {
          setClientApprovedAt(res.data.clientApprovedAt);
        }
        if (res.data) {
          setClientApprovedBy(res.data.clientApprovedBy || null);
          if (res.data.uploadedBy != null) {
            setUploadedBy(res.data.uploadedBy);
          }
          // Do not apply forgeStatus here. A slow getById can overwrite Retry's
          // IN_PROGRESS with a stale FAILED and make the page glitch.
        }
      } catch (err) {
        console.error('Error fetching file details:', err);
      }
    };
    fetchFileDetails();
  }, [fileId]);

  const startTranslation = async (overageApproved = null, forceRetranslate = false) => {
    const id = file.fileId || file.id;
    if (!id || isStorageOnlyFile) {
      toast.error('Please sync files from storage first before translating.');
      return;
    }

    try {
      setTranslating(true);
      setError(null);
      const response = await forgeApi.uploadToForge(id, {
        ...(overageApproved ? { overageApproved } : {}),
        ...(forceRetranslate ? { forceRetranslate: true } : {}),
      });
      const newStatus = response.data?.status || 'IN_PROGRESS';
      setTranslationStatus(newStatus);
      setUploadProgress(
        response.data?.uploadProgress !== undefined ? response.data.uploadProgress : 0
      );
      setTranslationProgress(response.data?.translationProgress ?? null);
      toast.success(forceRetranslate ? 'Re-translation started' : 'Translation started');
    } catch (err) {
      const payload = err.response?.data || {};
      const errorMessage = translationStartErrorMessage(payload, err.message);

      if (payload.error === 'USAGE_CONFIRMATION_REQUIRED' || payload.requiresConfirmation) {
        setOverageDialog({
          open: true,
          warnings: Array.isArray(payload.warnings) ? payload.warnings : [errorMessage],
          confirmationWord: payload.confirmationWord || 'APPROVE',
          typedValue: '',
          forceRetranslate: !!forceRetranslate,
        });
      } else if (payload.error === 'TRANSLATION_IN_PROGRESS') {
        toast.warning(
          payload.message ||
            'Maximum of 3 translations can run at the same time. Wait until one finishes, then try again.'
        );
      } else {
        toast.error(errorMessage, { autoClose: 10000, toastId: `translate-error-${id}-${Date.now()}` });
        setError(errorMessage);
        setTranslationStatus('FAILED');
        setUploadProgress(null);
        setTranslationProgress(null);
      }
    } finally {
      setTranslating(false);
    }
  };

  const closeOverageDialog = () => {
    setOverageDialog({
      open: false,
      warnings: [],
      confirmationWord: 'APPROVE',
      typedValue: '',
      forceRetranslate: false,
    });
  };

  const confirmOverageTranslate = async () => {
    const required = (overageDialog.confirmationWord || 'APPROVE').trim();
    if (overageDialog.typedValue.trim().toUpperCase() !== required.toUpperCase()) {
      toast.warning(`Type ${required.toLowerCase()} to continue.`);
      return;
    }
    const forceRetranslate = !!overageDialog.forceRetranslate;
    closeOverageDialog();
    await startTranslation(required.toUpperCase(), forceRetranslate);
  };

  const confirmRetranslate = async () => {
    setRetranslateDialogOpen(false);
    await startTranslation(null, true);
  };

  const loadViewer = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 FileViewer: loadViewer called');
      console.log('🔍 FileViewer: file object:', file);
      console.log('🔍 FileViewer: file.forgeStatus:', file.forgeStatus);
      console.log('🔍 FileViewer: file.fileType:', file.fileType);
      console.log('🔍 FileViewer: file.type:', file.type);
      console.log('🔍 FileViewer: translationStatus state:', translationStatus);

      // For RVT/DWG files, get Forge viewer URN
      if (fileType === 'RVT' || fileType === 'DWG') {
        if (isTranslated) {
          console.log('✅ FileViewer: Translation SUCCESS, loading Forge viewer');
          // Load Forge viewer
          await loadForgeViewer();
        } else if (isTranslationActive) {
          console.log('⏳ FileViewer: Translation status is not SUCCESS, checking status');
          // Check translation status
          await checkTranslationStatus();
        }
      } else if (file.fileType === 'PDF') {
        // For PDF files, use direct download endpoint through auth server gateway
        const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/';
        const fileId = file.fileId || file.id;
        setViewerUrl(`${baseUrl}fileshare/api/files/${fileId}/download`);
      } else {
        // For other file types (images, etc.), use direct download endpoint
        const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/';
        const fileId = file.fileId || file.id;
        setViewerUrl(`${baseUrl}fileshare/api/files/${fileId}/download`);
      }
    } catch (err) {
      setError('Failed to load viewer');
      console.error('Viewer error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadForgeViewer = async () => {
    try {
      const fileName = file.fileName || file.name;
      const forgeUrn = file.forgeUrn;
      
      console.log('Loading Forge viewer for:', fileName);
      console.log('Forge URN from file:', forgeUrn);
      
      // Get viewer configuration from backend (includes access token)
      const fileId = file.fileId || file.id;
      console.log('Getting viewer config for fileId:', fileId);
      
      const configResponse = await forgeApi.getViewerConfig(fileId);
      console.log('Viewer config received:', configResponse.data);
      
      // The ForgeViewer component will use this config to initialize
      setViewerUrl('forge-viewer-placeholder');
    } catch (err) {
      console.error('Failed to load Forge viewer config:', err);
      throw new Error('Failed to load Forge viewer: ' + err.message);
    }
  };

  const checkTranslationStatus = async () => {
    try {
      // Get fileId from file object (handle both fileId and id properties)
      const fileId = file.fileId || file.id;
      if (!fileId) {
        setError('File ID not found');
        return;
      }
      
      // Check translation status from API
      const response = await filesApi.getTranslationStatus(fileId);
      const status = response.data.status;
      const uploadProg = response.data.uploadProgress;
      const translationProg = response.data.translationProgress;

      console.log('📊 Progress data from API:', { 
        status, 
        uploadProgress: uploadProg, 
        translationProgress: translationProg,
        fullResponse: response.data 
      });

      setTranslationStatus(status);
      // Set progress even if 0 (upload might be starting)
      if (uploadProg !== undefined && uploadProg !== null) {
        setUploadProgress(uploadProg);
        console.log('📤 Set upload progress:', uploadProg);
      } else {
        console.log('📤 Upload progress is null/undefined');
      }
      if (translationProg !== undefined && translationProg !== null) {
        setTranslationProgress(translationProg);
        console.log('🔄 Set translation progress:', translationProg);
      } else {
        console.log('🔄 Translation progress is null/undefined');
      }

      if (status === 'SUCCESS') {
        setUploadProgress((prev) => (prev == null || prev < 100 ? 100 : prev));
        setTranslationProgress(100);
        await loadForgeViewer();
      } else if (status === 'FAILED') {
        setError(null);
        const failedMessage = translationStartErrorMessage(
          response.data || {},
          'Translation failed. You can retry this file.'
        );
        toast.error(failedMessage, {
          autoClose: 10000,
          toastId: `translate-failed-${fileId}`,
        });
      } else {
        // Still in progress, wait and check again
        setTimeout(checkTranslationStatus, 5000);
      }
    } catch (err) {
      console.error('Failed to check translation status:', err);
      const isTimeoutError = err.code === 'ECONNABORTED' || 
                             err.message?.includes('timeout') || 
                             err.message?.includes('Timeout');
      
      if (isTimeoutError) {
        // Timeout error - translation might still be in progress, check again
        console.warn('⏱️ Translation status check timed out. Retrying in 10 seconds...');
        setTimeout(checkTranslationStatus, 10000); // Wait 10 seconds and retry
      } else if (err.response?.status === 404) {
        // File not found - might be transient, retry
        console.warn('⚠️ Translation status not found (404). Retrying in 5 seconds...');
        setTimeout(checkTranslationStatus, 5000);
      } else if (err.response?.status >= 500) {
        // Server error - retry after longer delay
        console.error('❌ Server error checking translation status. Retrying in 15 seconds...');
        setTimeout(checkTranslationStatus, 15000);
      } else {
        // Other errors - show error but keep polling if status is IN_PROGRESS
        const errorMsg = err.response?.data?.message || err.message || 'Unknown error';
        console.error('❌ Failed to check translation status:', errorMsg);
        
        // If status is IN_PROGRESS, continue polling even on errors (might be transient)
        if (translationStatus === 'IN_PROGRESS' || translationStatus === 'Translating...') {
          console.info('🔄 Status is IN_PROGRESS, continuing to poll despite error...');
          setTimeout(checkTranslationStatus, 10000);
        } else {
          setError('Failed to check translation status: ' + errorMsg);
        }
      }
    }
  };

  const handleShare = async () => {
    try {
      console.log('🔍 FileViewer: Share button clicked, file:', file);
      console.log('🔍 FileViewer: Calling onShare with file');
      if (onShare) {
        onShare(file);
      } else {
        console.warn('⚠️ FileViewer: onShare prop not provided');
        alert('Share functionality not configured. Please report this issue.');
      }
    } catch (err) {
      console.error('Share error:', err);
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'RVT':
        return <Architecture sx={{ fontSize: 40, color: 'primary.main' }} />;
      case 'DWG':
        return <Engineering sx={{ fontSize: 40, color: 'secondary.main' }} />;
      case 'PDF':
        return <PictureAsPdf sx={{ fontSize: 40, color: 'error.main' }} />;
      case 'IMAGE':
        return <Image sx={{ fontSize: 40, color: 'success.main' }} />;
      default:
        return <Architecture sx={{ fontSize: 40, color: 'text.secondary' }} />;
    }
  };

  const getFileTypeColor = (fileType) => {
    switch (fileType) {
      case 'RVT':
        return 'primary';
      case 'DWG':
        return 'secondary';
      case 'PDF':
        return 'error';
      case 'IMAGE':
        return 'success';
      default:
        return 'default';
    }
  };

  const getTranslationStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS':
        return 'success';
      case 'IN_PROGRESS':
        return 'warning';
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {getFileIcon(file.fileType)}
          <Box>
            <Typography variant="h6">{file.fileName}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Chip
                label={file.fileType}
                size="small"
                color={getFileTypeColor(file.fileType)}
              />
              <Chip
                label={translationStatus}
                size="small"
                color={getTranslationStatusColor(translationStatus)}
                variant="outlined"
              />
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isCadFile && isTranslated && !isTranslationActive && (
            <Button
              variant="outlined"
              startIcon={translating ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
              onClick={() => setRetranslateDialogOpen(true)}
              disabled={translating || isStorageOnlyFile}
            >
              Re-translate
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Share />}
            onClick={handleShare}
          >
            Share
          </Button>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ mb: 2, flexShrink: 0 }} />

      {/* File Info */}
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Typography variant="body2" color="text.secondary">
          Size: {formatFileSize(file.fileSizeLatestVersion || file.fileSize || file.size || 0)} |
          Uploaded by: {uploadedBy || 'Unknown'} |
          Date: {formatAppDate(file.createdAt)}
        </Typography>
      </Box>

      {/* Untranslated CAD: clear message + Translate (no blank panel / no 404 viewer errors) */}
      {needsTranslation && (
        <Paper
          sx={{
            flex: 1,
            minHeight: 0,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            overflow: 'hidden',
          }}
        >
          <Architecture sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            This file is not translated yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 420 }}>
            {normalizedTranslationStatus === 'FAILED'
              ? 'The previous translation failed. Translate again to view this model.'
              : 'Translate this file to Autodesk Forge to open the 3D viewer.'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={translating ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
            onClick={() => startTranslation()}
            disabled={translating || isStorageOnlyFile}
          >
            {translating
              ? 'Starting...'
              : normalizedTranslationStatus === 'FAILED'
              ? 'Retry translation'
              : 'Translate'}
          </Button>
          {isStorageOnlyFile && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
              Sync folders first so this file is available in the database, then translate.
            </Typography>
          )}
        </Paper>
      )}

      {/* Show CAD viewer/progress only while translating or after translation succeeds. */}
      {shouldShowViewerArea && <Paper
        sx={{
          flex: 1,
          minHeight: 0,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>
              {translationStatus === 'IN_PROGRESS' ? 'Processing file...' : 'Loading viewer...'}
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : isTranslated ? (
          <Box sx={{ width: '100%', flex: 1, minHeight: 0, position: 'relative' }}>
            <ForgeViewer
              fileId={file.fileId || file.id}
              fileName={file.fileName || file.name}
              fileType={file.fileType || file.type}
              onError={(err) => setError('Failed to load 3D viewer: ' + err.message)}
            />
          </Box>
        ) : isTranslationActive || translationStatus === 'FAILED' || (uploadProgress !== null && uploadProgress !== undefined) || (translationProgress !== null && translationProgress !== undefined) ? (
          <Box sx={{ textAlign: 'center', width: '100%', maxWidth: 600, mx: 'auto' }}>
            <Stack spacing={2}>
              {(() => {
                const isFailed = translationStatus === 'FAILED';
                // URN means OSS upload finished even if uploadProgress was left stale (e.g. 10%).
                const uploadSucceeded =
                  Boolean(file.forgeUrn) ||
                  (uploadProgress != null && uploadProgress >= 100);
                const uploadFailed = isFailed && !uploadSucceeded;
                const displayUploadPct = uploadSucceeded
                  ? Math.max(uploadProgress ?? 0, 100)
                  : uploadProgress;
                const showUploadBar =
                  displayUploadPct != null &&
                  displayUploadPct >= 0 &&
                  (displayUploadPct < 100 ||
                    (displayUploadPct >= 100 &&
                      translationProgress != null &&
                      translationProgress < 100) ||
                    (isFailed && uploadSucceeded));
                const showTranslationBar =
                  translationProgress != null &&
                  translationProgress >= 0 &&
                  (translationProgress < 100 || isFailed);

                let uploadLabel = 'Uploading to Autodesk S3 ';
                let uploadColor = 'primary';
                if (uploadFailed) {
                  uploadLabel = 'Failed at upload: ';
                  uploadColor = 'error';
                } else if (uploadSucceeded || (displayUploadPct != null && displayUploadPct >= 100)) {
                  uploadLabel = 'Upload complete ';
                  uploadColor = 'success';
                }

                return (
                  <>
                    {isFailed && (
                      <Alert severity="error" sx={{ mb: 1 }}>
                        {uploadFailed
                          ? 'Upload to Autodesk failed. Last progress is shown below.'
                          : 'Upload succeeded, but Autodesk translation failed. Last translation progress is shown below.'}
                      </Alert>
                    )}
                    {showUploadBar && (
                      <Box>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2" fontWeight="bold">
                            {uploadLabel}
                            {(displayUploadPct ?? 0).toFixed(1)}%
                            {uploadSucceeded && !uploadFailed ? ' ✓' : ''}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, displayUploadPct ?? 0)}
                          color={uploadColor}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    )}
                    {showTranslationBar && (
                      <Box>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2" fontWeight="bold">
                            {isFailed && !uploadFailed
                              ? 'Failed at translation: '
                              : isFailed && uploadFailed
                              ? 'Translation: '
                              : 'Translating File '}
                            {translationProgress.toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, translationProgress)}
                          color={isFailed && !uploadFailed ? 'error' : 'secondary'}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    )}
                    {(uploadProgress == null) && (translationProgress == null) && !isFailed && (
                      <CircularProgress sx={{ mb: 2 }} />
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {isFailed
                        ? uploadFailed
                          ? 'You can retry the upload, or check the file and network.'
                          : 'You can retry translation, or check the Revit/DWG file in Autodesk.'
                        : uploadProgress != null && uploadProgress < 100
                        ? `Uploading file to Autodesk... ${uploadProgress.toFixed(1)}%`
                        : translationProgress != null && translationProgress < 100
                        ? `File is being translated for viewing... ${translationProgress.toFixed(1)}%`
                        : 'Processing file for viewing...'}
                    </Typography>
                  </>
                );
              })()}

              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadViewer}
                sx={{ mt: 1 }}
              >
                Refresh Status
              </Button>
            </Stack>
          </Box>
        ) : isCadFile ? (
          <Box sx={{ width: '100%', flex: 1, minHeight: 0, position: 'relative' }}>
            <ForgeViewer
              fileId={file.fileId || file.id}
              fileName={file.fileName || file.name}
              fileType={file.fileType || file.type}
              onError={(err) => setError('Failed to load 3D viewer: ' + err.message)}
            />
          </Box>
        ) : file.fileType === 'PDF' ? (
          <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {viewerUrl ? (
              <iframe
                src={`/pdfjs/viewer.html?file=${encodeURIComponent(viewerUrl)}&token=${encodeURIComponent(localStorage.getItem('token') || '')}&tenantId=${encodeURIComponent(localStorage.getItem('tenantId') || '')}&fileName=${encodeURIComponent(file.fileName || 'document.pdf')}`}
                title={file.fileName}
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: 320,
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                }}
              />
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                Loading PDF...
              </Typography>
            )}
          </Box>
        ) : file.fileType === 'IMAGE' ? (
          <Box sx={{ textAlign: 'center', width: '100%', height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Image Viewer
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Image viewer would be embedded here
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: 400,
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'grey.50',
              }}
            >
              <Typography color="text.secondary">
                Image Viewer Placeholder
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              File Preview Not Available
            </Typography>
            <Typography color="text.secondary">
              This file type cannot be previewed in the browser.
            </Typography>
          </Box>
        )}
      </Paper>}

      {/* Comments — always reserved at bottom (not clipped by Forge flex:1) */}
      <Paper variant="outlined" sx={{ mt: 2, flexShrink: 0, overflow: 'hidden' }}>
        <Button
          fullWidth
          onClick={() => setCommentsOpen(!commentsOpen)}
          startIcon={<CommentIcon />}
          endIcon={commentsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
        >
          Comments {comments.length > 0 && `(${comments.length})`}
        </Button>
        <Collapse in={commentsOpen}>
          <Box sx={{ p: 2, maxHeight: 180, overflow: 'auto' }}>
            {clientApprovedAt != null ? (
              <Typography variant="body2" color="success.main" sx={{ mb: 2 }} display="flex" alignItems="center" gap={0.5}>
                <CheckCircleIcon fontSize="small" />
                {clientApprovedBy
                  ? `Approved by ${clientApprovedBy} on ${formatAppDate(clientApprovedAt)}`
                  : `Approved on ${formatAppDate(clientApprovedAt)}`}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Not approved by client
              </Typography>
            )}
            {commentsLoading ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List dense disablePadding>
                {comments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No comments yet.
                  </Typography>
                ) : (
                  comments.map((c) => (
                    <ListItem key={c.commentId} alignItems="flex-start" disablePadding sx={{ mb: 1 }}>
                      <ListItemText
                        primary={c.text}
                        secondary={
                          <Typography component="span" variant="caption" color="text.secondary">
                            {c.authorDisplayName || c.authorType}
                            {c.authorType === 'CLIENT' && (
                              <Chip label="Client" size="small" sx={{ ml: 0.5, height: 18 }} />
                            )}
                            {' · '}
                            {c.createdAt ? new Date(c.createdAt).toLocaleString() : ''}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))
                )}
              </List>
            )}
          </Box>
        </Collapse>
      </Paper>

      <Dialog open={overageDialog.open} onClose={closeOverageDialog} fullWidth maxWidth="sm">
        <DialogTitle>Extra cost warning</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 0.5 }}>
            This translation exceeds a limit and may incur extra cost. Type{' '}
            <strong>
              {(overageDialog.confirmationWord || 'APPROVE').toLowerCase()}
            </strong>{' '}
            to continue.
          </Alert>
          {(overageDialog.warnings || [])
            .filter((warning) => !/\d+(\.\d+)?\s*(GB|MB|KB)\b/i.test(String(warning || '')))
            .map((warning) => (
            <Typography key={warning} variant="body2" sx={{ mb: 1 }}>
              {String(warning).replace(/\s*soft\s+/gi, ' ')}
            </Typography>
          ))}
          <TextField
            fullWidth
            autoFocus
            margin="dense"
            label={`Type ${(overageDialog.confirmationWord || 'APPROVE').toLowerCase()} to confirm`}
            value={overageDialog.typedValue}
            onChange={(e) =>
              setOverageDialog((prev) => ({ ...prev, typedValue: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOverageDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={confirmOverageTranslate}
            disabled={
              overageDialog.typedValue.trim().toUpperCase() !==
              (overageDialog.confirmationWord || 'APPROVE').toUpperCase()
            }
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={retranslateDialogOpen} onClose={() => setRetranslateDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Re-translate file?</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 0.5 }}>
            This starts a <strong>new Autodesk translation job</strong> for the current synced file.
            It counts toward translation usage. The 3D viewer will show a processing state until the new translation finishes; then the updated model loads.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetranslateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={confirmRetranslate} disabled={translating}>
            Re-translate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FileViewer;

